import type { Notification } from '@ralph/contracts'
import clsx from 'clsx'
import { Bell, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { notificationDestination } from '@/components/marketplace/consumer/notification-destination.ts'
import { notificationText } from '@/components/marketplace/consumer/notification-text.ts'
import { useFocusTrap } from '@/components/ui/focus-trap.ts'
import { Overlay } from '@/components/ui/Overlay.tsx'
import { Button, EmptyState, IconButton } from '@/components/ui/primitives.tsx'
import { useMarkAllNotificationsRead, useMarkNotificationRead, useNotifications } from '@/hooks/use-marketplace.ts'
import { useI18n, useT } from '@/i18n/index.ts'

/**
 * The shell's notification inbox (AGM-049).
 *
 * The unread dot is `--alert`, and that is the one decorative-looking use of a signal colour
 * that is not decoration: an unread notification is an unmet consequence — something happened
 * to an agent this person owns or uses and nobody has looked at it yet. It is deliberately
 * not the primary channel, which in this palette means the model and things happening right
 * now. Everything else in this drawer is neutral ink.
 *
 * The drawer is a modal dialog rather than a popover because it is a reading surface, not a
 * menu: it holds multi-line prose, it is reached from a rail that cannot host it, and a
 * keyboard user needs Escape and a focus trap either way. It also has to be able to close
 * itself before anything else opens — the builder page queries `getByRole('dialog')`
 * unqualified, so two mounted dialogs are a broken page, not a layered one.
 */
export const NotificationsBell = ({ enabled }: { enabled: boolean }) => {
  const i18n = useI18n()
  // In the body, like every catalogue access: the unread count is a plural, and a frozen
  // pack would keep choosing English forms after a switch (PRD-27 F-3, LOC-012).
  const t = i18n.t.marketplace.gallery.notifications
  const [open, setOpen] = useState(false)
  const notifications = useNotifications(enabled)
  const unread = notifications.data?.unread ?? 0

  return (
    <>
      <button
        type="button"
        aria-label={unread > 0 ? t.openWithUnread(i18n, unread) : t.open}
        title={t.open}
        onClick={() => setOpen(true)}
        className="relative flex h-[33px] w-[33px] shrink-0 items-center justify-center rounded-full border border-rule bg-card text-fg-muted transition-[background-color,border-color,color] duration-200 hover:border-rule-strong hover:bg-card-solid hover:text-fg"
      >
        <Bell size={14} strokeWidth={1.9} aria-hidden />
        {unread > 0 && (
          // The count itself is in the button's accessible name; the dot is what a sighted
          // reader sees, and it carries no meaning a screen reader is not already told.
          <span
            aria-hidden
            className="absolute top-[6px] right-[7px] h-[7px] w-[7px] rounded-full border-[1.5px] border-shell-solid bg-alert"
          />
        )}
      </button>

      {open && <NotificationsDrawer onClose={() => setOpen(false)} />}
    </>
  )
}

const NotificationsDrawer = ({ onClose }: { onClose: () => void }) => {
  const { formatAgo } = useI18n()
  const messages = useT()
  const t = messages.marketplace.gallery.notifications
  const notifications = useNotifications(true)
  const markRead = useMarkNotificationRead()
  const markAllRead = useMarkAllNotificationsRead()
  const navigate = useNavigate()
  const panelRef = useRef<HTMLDivElement>(null)
  useFocusTrap(panelRef, { onEscape: onClose })

  const items = notifications.data?.items ?? []
  const unread = notifications.data?.unread ?? 0

  // Every kind opens where its reader acts on it (PRD-26 D-31); one with nowhere to go — a role
  // granted, say — is only marked read, and the drawer stays open on the list.
  const open = (notification: Notification) => {
    if (!notification.readAt) markRead.mutate(notification.id)
    const destination = notificationDestination(notification)
    if (destination === null) return
    onClose()
    navigate(destination)
  }

  return (
    <Overlay>
      <div className="fade-in fixed inset-0 z-50 flex justify-end bg-[var(--scrim)]/35 p-2 backdrop-blur-[2px] sm:p-3">
        {/* The backdrop closes on click; it carries no label because the panel's own close
            button is the announced control. */}
        <button type="button" aria-hidden tabIndex={-1} className="absolute inset-0 cursor-default" onClick={onClose} />

        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="notifications-title"
          tabIndex={-1}
          // `shadow-frame` is a utility and therefore beats the base layer's focus ring, which
          // is what keeps the trap's programmatic focus from drawing a ring round the whole
          // panel the moment it opens.
          className="relative flex h-full w-full max-w-[380px] flex-col overflow-clip rounded-panel border border-rule bg-card-solid shadow-frame focus:outline-none"
        >
          <header className="flex items-center gap-2 border-b border-rule px-4 py-3">
            <h2 id="notifications-title" className="text-[16px]">
              {t.title}
            </h2>
            {unread > 0 && (
              <Button variant="ghost" size="sm" onClick={() => markAllRead.mutate()} disabled={markAllRead.isPending}>
                {t.markAllRead}
              </Button>
            )}
            <IconButton label={t.close} size={27} className="ml-auto" onClick={onClose}>
              <X size={13} aria-hidden />
            </IconButton>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {notifications.isError && (
              <p role="alert" className="px-4 py-4 text-[13px] text-alert">
                {t.failed}
              </p>
            )}

            {!notifications.isError && items.length === 0 && !notifications.isLoading && (
              <EmptyState title={t.empty.title} body={t.empty.body} />
            )}

            <ul>
              {items.map((notification) => {
                // Rendered HERE, on read, and not where it was written: the sentence follows
                // the reader's language, and a locale switch re-renders the whole inbox rather
                // than leaving it frozen behind (PRD-27 LOC-035).
                const said = notificationText(messages, notification)
                return (
                  <li key={notification.id} className="border-b border-rule/60 last:border-b-0">
                    <button
                      type="button"
                      onClick={() => open(notification)}
                      className={clsx(
                        // `focus-outline`: the drawer clips its content, so the spread ring is
                        // cut off at the row edges and reads as a divider rather than as focus.
                        'focus-outline block w-full px-4 py-3 text-left transition-colors duration-200 hover:bg-card',
                        !notification.readAt && 'bg-card/70'
                      )}
                    >
                      <span className="flex items-start gap-2.5">
                        <span
                          aria-hidden
                          className={clsx(
                            'mt-[7px] h-[6px] w-[6px] shrink-0 rounded-full',
                            notification.readAt ? 'bg-transparent' : 'bg-alert'
                          )}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-[13px] leading-snug font-semibold tracking-[-0.012em]">
                            {said.subject}
                            {!notification.readAt && <span className="sr-only"> — {t.unreadDot}</span>}
                          </span>
                          <span className="mt-0.5 block text-pretty text-[12.5px] leading-relaxed text-fg-muted">
                            {said.body}
                          </span>
                          <span className="readout mt-1.5 block text-fg-subtle">
                            {formatAgo(notification.createdAt)}
                          </span>
                        </span>
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      </div>
    </Overlay>
  )
}
